import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, MapPin, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { COMPANY } from "@/data/site";

const EASE = [0.22, 1, 0.36, 1];

const OFFICES = [
    { c: "Bhubaneswar (Head)", a: "MGM Heights, Kalinga Nagar" },
    { c: "Cuttack", a: "CDA Sector 6" },
    { c: "Puri", a: "New Marine Drive" },
    { c: "Rourkela", a: "Civil Township" },
    { c: "Sambalpur", a: "Ainthapali" },
    { c: "Berhampur", a: "Aska Road" },
];

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", phone: "", project: "", message: "" });
    const [sending, setSending] = useState(false);

    const submit = (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
            toast.error("Please fill your name, email and a short message.");
            return;
        }
        setSending(true);
        setTimeout(() => {
            setSending(false);
            toast.success("Thank you. A concierge will write back within 24 hours.");
            setForm({ name: "", email: "", phone: "", project: "", message: "" });
        }, 900);
    };

    const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    return (
        <div data-testid="page-contact">
            <PageHeader
                eyebrow="Contact — the studio"
                title={<>Say hello. <em className="italic text-lime">Slowly.</em></>}
                lead="For enquiries, site visits, press requests or long conversations about a plot of land you own — write to us. A concierge will reply within 24 hours."
            />

            <section className="relative bg-forest-950 py-20 md:py-32">
                <div className="mx-auto max-w-[1440px] px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
                    <div className="md:col-span-5 space-y-10">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.9, ease: EASE }}
                        >
                            <div className="font-mono-mini text-[10px] tracking-[0.32em] uppercase text-lime mb-4">
                                Head office · Bhubaneswar
                            </div>
                            <div className="space-y-4 text-ivory/85">
                                <div className="flex items-start gap-3">
                                    <MapPin size={18} className="text-lime mt-1 shrink-0" />
                                    <div>
                                        <div className="font-serif-display text-xl">MGM Heights, Plot 140/9957</div>
                                        <div className="text-ivory/60 text-sm">
                                            K Living — 3rd floor, Kalinga Nagar<br />
                                            Bhubaneswar, Khurdha — 751003, Odisha<br />
                                            Plus Code: {COMPANY.address.plusCode}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Phone size={18} className="text-lime shrink-0" />
                                    <a href="tel:+916740000000" className="hover-underline">+91 674 000 0000</a>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Mail size={18} className="text-lime shrink-0" />
                                    <a href="mailto:hello@kliving.co" className="hover-underline">hello@kliving.co</a>
                                </div>
                            </div>
                        </motion.div>

                        <div>
                            <div className="font-mono-mini text-[10px] tracking-[0.32em] uppercase text-lime mb-4">
                                Six offices · Odisha
                            </div>
                            <ul className="divide-y divide-white/10 border-y border-white/10">
                                {OFFICES.map((x, i) => (
                                    <li key={x.c} data-testid={`office-city-${i}`} className="flex items-center justify-between py-4">
                                        <div className="font-serif-display text-lg text-ivory">{x.c}</div>
                                        <div className="text-ivory/50 text-sm">{x.a}</div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    <motion.form
                        onSubmit={submit}
                        data-testid="contact-form"
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.1, ease: EASE }}
                        className="md:col-span-7 md:pl-8 lg:pl-14"
                    >
                        <div className="font-mono-mini text-[10px] tracking-[0.32em] uppercase text-lime mb-4">
                            Write to us
                        </div>
                        <h2 className="font-serif-display text-4xl md:text-5xl leading-tight text-ivory mb-10">
                            A few lines is enough.
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
                            {[
                                { k: "name", l: "Your name", t: "text", req: true },
                                { k: "email", l: "Email", t: "email", req: true },
                                { k: "phone", l: "Phone (optional)", t: "tel" },
                                { k: "project", l: "Project of interest", t: "text" },
                            ].map((f) => (
                                <label key={f.k} className="block">
                                    <span className="block font-mono-mini text-[10px] tracking-[0.32em] uppercase text-ivory/50 mb-3">
                                        {f.l}
                                    </span>
                                    <input
                                        data-testid={`contact-input-${f.k}`}
                                        type={f.t}
                                        value={form[f.k]}
                                        onChange={onChange(f.k)}
                                        required={f.req}
                                        className="w-full bg-transparent border-b border-ivory/20 focus:border-lime outline-none py-3 text-ivory placeholder-ivory/30 text-lg transition-colors"
                                    />
                                </label>
                            ))}
                        </div>

                        <label className="block mt-8">
                            <span className="block font-mono-mini text-[10px] tracking-[0.32em] uppercase text-ivory/50 mb-3">
                                Your message
                            </span>
                            <textarea
                                data-testid="contact-input-message"
                                value={form.message}
                                onChange={onChange("message")}
                                required
                                rows={4}
                                className="w-full bg-transparent border-b border-ivory/20 focus:border-lime outline-none py-3 text-ivory placeholder-ivory/30 text-lg resize-none transition-colors"
                            />
                        </label>

                        <div className="mt-10 flex items-center justify-between gap-6">
                            <p className="text-ivory/45 text-sm max-w-sm">
                                By writing to us you agree to be contacted about your enquiry. We
                                do not share your details, ever.
                            </p>
                            <button
                                type="submit"
                                data-testid="contact-submit"
                                disabled={sending}
                                className="group inline-flex items-center gap-5 bg-lime text-forest-900 pl-8 pr-4 py-4 hover:bg-lime-hi transition-colors disabled:opacity-60"
                            >
                                <span className="font-mono-mini text-[11px] tracking-[0.32em] uppercase">
                                    {sending ? "Sending" : "Send message"}
                                </span>
                                <span className="h-10 w-10 bg-forest-900 text-lime flex items-center justify-center group-hover:rotate-45 transition-transform duration-500">
                                    <ArrowUpRight size={16} strokeWidth={1.5} />
                                </span>
                            </button>
                        </div>
                    </motion.form>
                </div>
            </section>

            {/* Map */}
            <section className="relative bg-forest-900">
                <div className="relative w-full aspect-[16/8] md:aspect-[16/6] overflow-hidden border-t border-white/10">
                    <iframe
                        data-testid="office-map"
                        title="KLiving Bhubaneswar office"
                        src="https://www.google.com/maps?q=MGM+Heights+Kalinga+Nagar+Bhubaneswar&output=embed"
                        className="absolute inset-0 h-full w-full grayscale contrast-[0.95] opacity-90"
                        style={{ border: 0, filter: "hue-rotate(90deg) grayscale(0.5) brightness(0.6)" }}
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                    />
                </div>
            </section>
        </div>
    );
}
